import Box from '@mui/material/Box';

import { RevealGroup } from '@/components/atoms';
import { type ContentItem } from '@/content/site';

import { ImportPromiseProof } from './ImportPromiseProof';

/**
 * The three proof points as one set: a divided editorial list at every width,
 * arriving in sequence as the band is scrolled to.
 *
 * `RevealGroup` rather than three `Reveal`s, which is one observer instead of
 * three and gives the stagger for free. It has to *be* the grid and not wrap
 * one, because the stagger is `nth-of-type` on its own direct children — put
 * a container between them and all three inherit the first child's delay.
 * That rules out MUI's `Grid`, whose items are the children it would own, so
 * the three columns are CSS grid here instead. Equal columns by construction,
 * so a longer proof cannot make its neighbours shorter.
 *
 * Each row has its own quiet divider; the set gets a leading divider from the
 * band layout on wide screens, where it sits beside the copy.
 */
export function ImportPromiseProofs({ proofs }: { proofs: readonly ContentItem[] }) {
  return (
    <RevealGroup
      step={90}
      sx={{
        flex: '1 1 52%',
        minWidth: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: 0,
        pl: { md: 8 },
        borderLeft: { md: 1 },
        borderColor: { md: 'divider' },
      }}
    >
      {proofs.map((proof) => (
        <Box
          key={proof.title}
          sx={{
            py: { xs: 5, md: 6 },
            borderTop: 1,
            borderColor: 'divider',
            '&:first-of-type': { pt: 0, borderTop: 0 },
            '&:last-of-type': { pb: 0 },
          }}
        >
          <ImportPromiseProof {...proof} />
        </Box>
      ))}
    </RevealGroup>
  );
}
